import React from "react";
import { useFlags } from "flagsmith/react";
import PixMobileModel from "@pages/User/MyOrders/Sections/PixMobileModel";
import { usePixByOrder } from "@pages/User/MyOrders/hook/usePixByOrder";
import { PixPayload } from "@pages/User/MyOrders/models/IPixPayment";
import { mapOrderStatus } from "@pages/User/MyOrders/utilsOrders/utilsMyOrders";
import PixPaymentModal from "@components/PixPaymentModal";
import { usePixPaymentModal } from "@components/PixPaymentModal/hooks/usePixPaymentModal";
import { IconButton, Tooltip } from "@material-ui/core";
import QrCodeIcon from "@material-ui/icons/CropFree";

type Props = {
  orderNumber: string;
  razaoSocial: string;
  produto: string;
  valorTotal: number;
  payCond?: number;
  productCode?: string;
  volume?: string;
  deliveryDate?: string;
  orderStatus?: string;
  bp?: string;
};

const PixArea: React.FC<Props> = ({
  orderNumber,
  razaoSocial,
  produto,
  valorTotal,
  payCond,
  productCode = "",
  volume = "",
  deliveryDate = "",
  orderStatus,
  bp,
}) => {
  const { pix_method_paycond, pix_modal_v2 } = useFlags([
    "pix_method_paycond",
    "pix_modal_v2",
  ]);

  const isPixEnabled = Boolean(pix_method_paycond?.enabled) && payCond === 0;
  const isNewModalEnabled = Boolean(pix_modal_v2?.enabled);

  // Hook do modal novo — só executa se a flag estiver ativa
  const {
    open: modalOpen,
    openModal,
    closeModal,
    loading: modalLoading,
    refreshing,
    payload,
    paymentStatus,
    error: pixModalError,
    refreshQrCode,
  } = usePixPaymentModal({
    salesOrderNumber: orderNumber,
    bp: bp,
    enabled: isNewModalEnabled && isPixEnabled,
  });

  // Hook da versão antiga
  const { loading, payloads, error } = usePixByOrder(
    !isNewModalEnabled && isPixEnabled ? orderNumber : null
  );

  if (!isPixEnabled) {
    return (
      <Tooltip title="Disponível apenas para pagamento à vista">
        <span>
          <IconButton size="small" disabled aria-label="Pix indisponível">
            <QrCodeIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  // Flag ativa → modal novo integrado com dados reais da API
  if (isNewModalEnabled) {
    return (
      <>
        <Tooltip title="Pagar com Pix">
          <IconButton
            size="small"
            color="primary"
            onClick={openModal}
            aria-label="Abrir pagamento Pix"
          >
            <QrCodeIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <PixPaymentModal
          open={modalOpen}
          onClose={closeModal}
          orderNumber={orderNumber}
          orderStatus={mapOrderStatus(orderStatus)}
          customerName={razaoSocial}
          productCode={productCode}
          productName={produto}
          volume={volume}
          deliveryDate={deliveryDate}
          qrCodeValue={payload?.copiaECola || ""}
          pixCopiaECola={payload?.copiaECola || ""}
          amount={payload?.valor || valorTotal}
          receiver={payload?.receiverName}
          bank={payload?.bankName}
          transactionId={payload?.transactionId}
          issueDate={payload?.issueDate}
          paymentStatus={paymentStatus}
          onRefreshQrCode={refreshQrCode}
          loading={modalLoading}
          refreshing={refreshing}
          error={pixModalError}
        />
      </>
    );
  }

  // Flag inativa → versão antiga (PixMobileModel)
  return (
    <PixMobileModel
      razaoSocial={razaoSocial}
      produto={produto}
      valorTotal={valorTotal}
      payCond={payCond}
      payloads={payloads as PixPayload[]}
      loading={loading}
      error={error}
    />
  );
};

export default PixArea;
